import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Clock, Phone } from "lucide-react";
import { buildAdminApi } from "../../lib/admin";

const statusMeta = {
    pending: { color: "bg-[#FF4F18]", label: "New" },
    ready: { color: "bg-[#1B995E]", label: "Ready" },
    completed: { color: "bg-[#8C7A72]", label: "Done" },
};

const AdminOrders = () => {
    const { slug } = useParams();
    const adminApi = useMemo(() => buildAdminApi(slug), [slug]);
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        const load = () => adminApi.get("/admin/orders").then((r) => setOrders(r.data.orders));
        load();
        const int = setInterval(load, 15000);
        return () => clearInterval(int);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    return (
        <div>
            <div className="flex items-baseline justify-between mb-6">
                <h1 className="font-display font-black text-3xl">Orders</h1>
                <div className="text-xs text-[#8C7A72] uppercase tracking-[0.25em]">
                    {orders.length} recent
                </div>
            </div>

            {orders.length === 0 && (
                <div className="rounded-3xl bg-white border border-[#E8DCCB] p-10 text-center text-[#8C7A72]">
                    No orders yet.
                </div>
            )}

            <div className="flex flex-col gap-3">
                {orders.map((o) => {
                    const t = new Date(o.pickup_ready_at);
                    const timeStr = t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                    const created = new Date(o.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
                    const s = statusMeta[o.status] || statusMeta.pending;
                    return (
                        <div key={o.id} data-testid={`order-${o.order_number}`}
                            className="rounded-2xl bg-white border border-[#E8DCCB] p-4 flex items-start gap-4 flex-wrap sm:flex-nowrap">
                            <div className="min-w-[80px]">
                                <div className="font-display font-black text-lg">{o.order_number}</div>
                                {o.customer_name && (
                                    <div className="text-xs font-display font-bold text-[#FF4F18] mt-1">
                                        {o.customer_name}
                                    </div>
                                )}
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8C7A72] flex items-center gap-1 mt-1">
                                    <Phone className="h-3 w-3" strokeWidth={2.5} />
                                    ···{o.phone_last4}
                                </div>
                            </div>
                            <div className="flex-1 min-w-[200px]">
                                <div className="text-sm">
                                    {o.items.map((i, idx) => (
                                        <span key={idx}>
                                            {i.qty}× {i.name}{i.size ? ` (${i.size})` : ""}
                                            {idx < o.items.length - 1 ? ", " : ""}
                                        </span>
                                    ))}
                                </div>
                                <div className="text-xs text-[#8C7A72] mt-1 flex items-center gap-3 flex-wrap">
                                    <span className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" strokeWidth={2.5} />
                                        Pickup {timeStr}
                                    </span>
                                    <span>·</span>
                                    <span>{created}</span>
                                    <span>·</span>
                                    <span>{o.payment_method === "counter" ? "Counter" : "Online"}</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-display font-black">${o.total.toFixed(2)}</div>
                                <div className={`inline-block mt-1 ${s.color} text-white text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full font-bold`}>
                                    {s.label}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AdminOrders;
